import {
    type CSSProperties,
    type ReactNode,
    useContext,
    useState
} from "react";
import {
    type ViewStyle,
    StyleSheet,
    Platform,
    View
} from "react-native";
import {
    NCoreUIKitTheme,
    getCornerStyle,
    CodeViewer,
    Breadcrumb,
    HoverCard,
    StatTile,
    Alert,
    Table,
    Card,
    Icon,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    useDocsRouter,
    DocsLink
} from "../router";
import {
    ChevronRight,
    ChevronLeft
} from "lucide-react-native";
import {
    type DocsNavItem,
    DOCS_PAGES
} from "../nav";
import {
    DocsWidthContext
} from "../theme";

export type DocsStyleTokens = ReturnType<typeof NCoreUIKitTheme.useContext>;

export type DocsBreadcrumb = {
    href?: string;
    label: string;
};

const CODE_LANGUAGES: Record<string, string> = {
    jsx: "javascript",
    tsx: "typescript",
    js: "javascript",
    ts: "typescript",
    sh: "bash"
};

const stylesheet = StyleSheet.create({
    stage: {
        justifyContent: "center",
        alignContent: "center",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    stack: {
        marginHorizontal: "auto",
        flexDirection: "column",
        alignItems: "stretch",
        flexWrap: "nowrap"
    },
    list: {
        flexDirection: "column",
        display: "flex",
        margin: 0
    },
    column: {
        flexDirection: "column"
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    caption: {
        textAlign: "center"
    },
    frame: {
        overflow: "hidden",
        width: "100%"
    },
    reset: {
        margin: 0,
        padding: 0
    }
});

const TERM_PATTERN = /\{\{term:([a-zA-Z0-9]+)\}\}/;

const STAGE_FIT_STYLE = (Platform.OS === "web" ? {
    width: "fit-content",
    minWidth: "100%"
} : {}) as ViewStyle;

const domStyles: Record<"article" | "anchor" | "scroller" | "crumbs", CSSProperties> = {
    anchor: {
        textDecoration: "none"
    },
    scroller: {
        alignSelf: "stretch",
        overflowY: "hidden",
        overflowX: "auto"
    },
    crumbs: {
        overflow: "hidden",
        minWidth: 0
    },
    article: {
        display: "flex",
        minWidth: 0
    }
};

const getHeadingProps = (level: number) => ({
    role: "heading" as const,
    "aria-level": level
});

export const Article = ({
    children
}: {
    children: ReactNode;
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const isFullWidth = useContext(DocsWidthContext);

    return <article
        style={{
            ...stylesheet.column as object,
            ...domStyles.article,
            maxWidth: isFullWidth ? "100%" : shapeConfigs.contentMaxWidth,
            gap: spaces.spacingXxl
        }}
    >
        {children}
    </article>;
};

export const Caption = ({
    children
}: {
    children: ReactNode;
}) => {
    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    const isFullWidth = useContext(DocsWidthContext);

    return <Text
        style={[
            stylesheet.caption,
            {
                maxWidth: isFullWidth ? undefined : shapeConfigs.contentMaxWidth
            }
        ]}
        variant="bodyMediumSize"
        color="mid"
    >
        {children}
    </Text>;
};

export const PageHeader = ({
    breadcrumbs,
    aside,
    title,
    lead
}: {
    breadcrumbs: Array<DocsBreadcrumb>;
    aside?: string;
    title: string;
    lead: string;
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        getHref
    } = useDocsRouter();

    const isFullWidth = useContext(DocsWidthContext);

    return <header
        style={{
            ...stylesheet.column as object,
            gap: spaces.spacingMd,
            display: "flex"
        }}
    >
        <div
            style={domStyles.crumbs}
        >
            <Breadcrumb
                compactBehaviour="collapse"
                size="medium"
            >
                <Breadcrumb.List>
                    {
                        breadcrumbs.map((crumb, index) => <Breadcrumb.Item
                            key={`${crumb.label}-${index}`}
                        >
                            {
                                crumb.href ?
                                    <Breadcrumb.Link
                                        href={getHref(crumb.href)}
                                        title={crumb.label}
                                    />
                                :
                                    <Text
                                        variant="bodyMediumSize"
                                        color="mid"
                                    >
                                        {crumb.label}
                                    </Text>
                            }
                        </Breadcrumb.Item>)
                    }
                    <Breadcrumb.Item>
                        <Breadcrumb.Page
                            title={title}
                        />
                    </Breadcrumb.Item>
                </Breadcrumb.List>
            </Breadcrumb>
        </div>
        <View
            style={[
                stylesheet.row,
                {
                    columnGap: spaces.spacingMd,
                    rowGap: spaces.spacingXxs
                }
            ]}
        >
            <Text
                {...getHeadingProps(1)}
                variant="headlineLargeSize"
                color="high"
            >
                {title}
            </Text>
            {
                aside ?
                    <Text
                        variant="monoMediumSize"
                        color="low"
                    >
                        {aside}
                    </Text>
                :
                    null
            }
        </View>
        <Text
            style={{
                maxWidth: isFullWidth ? undefined : shapeConfigs.contentMaxWidth
            }}
            variant="bodyLargeSize"
            color="mid"
        >
            {lead}
        </Text>
    </header>;
};

export const SubTitle = ({
    children
}: {
    children: ReactNode;
}) => {
    return <Text
        {...getHeadingProps(3)}
        variant="titleMediumSize"
        color="high"
    >
        {children}
    </Text>;
};

export const Section = ({
    children,
    title,
    note,
    id
}: {
    children: ReactNode;
    note?: string;
    title: string;
    id: string;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        isRevealed,
        setIsRevealed
    ] = useState(false);

    return <section
        style={{
            ...stylesheet.column as object,
            gap: spaces.spacingMd,
            display: "flex"
        }}
        aria-labelledby={`${id}-title`}
        id={id}
    >
        <div
            onMouseLeave={() => setIsRevealed(false)}
            onMouseEnter={() => setIsRevealed(true)}
            onFocus={() => setIsRevealed(true)}
            onBlur={() => setIsRevealed(false)}
            style={{
                columnGap: spaces.spacingMd,
                rowGap: spaces.spacingXxs,
                alignItems: "baseline",
                flexWrap: "wrap",
                display: "flex"
            }}
        >
            <Text
                {...getHeadingProps(2)}
                variant="titleLargeSize"
                nativeID={`${id}-title`}
                color="high"
            >
                {title}
            </Text>
            {
                note ?
                    <Text
                        variant="bodyMediumSize"
                        color="low"
                    >
                        {note}
                    </Text>
                :
                    null
            }
            <DocsLink
                style={{
                    ...domStyles.anchor,
                    opacity: isRevealed ? 1 : 0
                }}
                aria-label={`${title} #`}
                href={`#${id}`}
            >
                <Text
                    variant="bodyLargeSize"
                    color="low"
                >
                    #
                </Text>
            </DocsLink>
        </div>
        {children}
    </section>;
};

export const Paragraph = ({
    isMuted = false,
    children
}: {
    children: ReactNode;
    isMuted?: boolean;
}) => {
    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    const isFullWidth = useContext(DocsWidthContext);

    return <Text
        style={{
            maxWidth: isFullWidth ? undefined : shapeConfigs.contentMaxWidth
        }}
        color={isMuted ? "mid" : "high"}
        variant="bodyLargeSize"
    >
        {children}
    </Text>;
};

export const Term = ({
    id
}: {
    id: string;
}) => {
    const {
        t
    } = useDocsI18n();

    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <HoverCard
        align="start"
        side="bottom"
    >
        <HoverCard.Trigger
            title={t(`glossary.${id}.label`)}
        />
        <HoverCard.Content
            aria-label={t(`glossary.${id}.title`)}
            summary={t(`glossary.${id}.text`)}
        >
            <View
                style={[
                    stylesheet.column,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    variant="titleSmallSize"
                    color="high"
                >
                    {t(`glossary.${id}.title`)}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t(`glossary.${id}.text`)}
                </Text>
            </View>
        </HoverCard.Content>
    </HoverCard>;
};

export const Prose = ({
    isMuted = false,
    text
}: {
    isMuted?: boolean;
    text: string;
}) => {
    const parts = text.split(TERM_PATTERN);

    return <Paragraph
        isMuted={isMuted}
    >
        {
            parts.map((part, index) => index % 2 === 1 ? <Term
                key={`${part}-${index}`}
                id={part}
            /> : part)
        }
    </Paragraph>;
};

export const BulletList = ({
    isOrdered = false,
    items
}: {
    items: Array<ReactNode>;
    isOrdered?: boolean;
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const isFullWidth = useContext(DocsWidthContext);

    const ListTag = isOrdered ? "ol" : "ul";

    return <ListTag
        style={{
            ...stylesheet.list as object,
            maxWidth: isFullWidth ? undefined : shapeConfigs.contentMaxWidth,
            paddingInlineStart: spaces.spacingLg,
            gap: spaces.spacingSm
        }}
    >
        {
            items.map((item, index) => <li
                key={index}
            >
                <Text
                    variant="bodyLargeSize"
                    color="high"
                >
                    {item}
                </Text>
            </li>)
        }
    </ListTag>;
};

export const InlineCode = ({
    children
}: {
    children: ReactNode;
}) => {
    const {
        shapeConfigs,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <Text
        style={{
            backgroundColor: colors.content.container.subtle,
            paddingHorizontal: spaces.spacingXs,
            paddingVertical: spaces.spacingXxs,
            ...getCornerStyle({
                radius: radiuses.xs,
                shapeConfigs
            })
        }}
        variant="monoMediumSize"
        color="high"
    >
        {children}
    </Text>;
};

export const CodeBlock = ({
    maxHeight,
    language,
    code
}: {
    maxHeight?: number;
    language: string;
    code: string;
}) => {
    return <CodeViewer
        language={CODE_LANGUAGES[language] ?? language}
        isLineNumberVisible={false}
        languageLabel={language}
        maxHeight={maxHeight}
        code={code}
    />;
};

export const Callout = ({
    children,
    title
}: {
    children: ReactNode;
    title?: string;
}) => {
    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    const isFullWidth = useContext(DocsWidthContext);

    const {
        t
    } = useDocsI18n();

    return <View
        style={{
            maxWidth: isFullWidth ? undefined : shapeConfigs.contentMaxWidth
        }}
    >
        <Alert
            title={title ?? t("callout.note")}
            announceBehaviour="none"
            description={children}
            variant="tonal"
            type="neutral"
        />
    </View>;
};

export const DemoFrame = ({
    children,
    height
}: {
    children: ReactNode;
    height?: number;
}) => {
    return <Card
        spreadBehaviour="stretch"
        style={stylesheet.frame}
        variant="outline"
    >
        <Card.Content
            isFlush={true}
        >
            <View
                style={[
                    stylesheet.frame,
                    height === undefined ? undefined : {
                        height
                    }
                ]}
            >
                {children}
            </View>
        </Card.Content>
    </Card>;
};

export const Preview = ({
    isStacked = false,
    children,
    caption
}: {
    isStacked?: boolean;
    children: ReactNode;
    caption?: string;
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <figure
        style={{
            ...stylesheet.list as object,
            gap: spaces.spacingMd
        }}
    >
        <Card
            spreadBehaviour="stretch"
            variant="inset"
        >
            <Card.Content
                isFlush={true}
            >
                <div
                    style={domStyles.scroller}
                >
                    <View
                        style={[
                            stylesheet.stage,
isStacked ? stylesheet.stack : undefined,
{
    minHeight: shapeConfigs.contentMaxWidth / 4,
    paddingHorizontal: spaces.spacingXl,
    paddingVertical: spaces.spacingXxl,
    gap: spaces.spacingXl
},
isStacked ? {
    maxWidth: shapeConfigs.contentMaxWidth / 2
} : STAGE_FIT_STYLE
                        ]}
                    >
                        {children}
                    </View>
                </div>
            </Card.Content>
        </Card>
        {
            caption ?
                <figcaption
                    style={stylesheet.reset as CSSProperties}
                >
                    <Caption>
                        {caption}
                    </Caption>
                </figcaption>
            :
                null
        }
    </figure>;
};

export const DocsTable = ({
    headers,
    rows
}: {
    rows: Array<Array<ReactNode>>;
    headers: Array<string>;
}) => {
    return <Table
        size="medium"
    >
        <Table.Header>
            <Table.Row>
                {
                    headers.map((header) => <Table.Head
                        key={header}
                    >
                        {header}
                    </Table.Head>)
                }
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {
                rows.map((row, rowIndex) => <Table.Row
                    key={rowIndex}
                >
                    {
                        row.map((cell, cellIndex) => <Table.Cell
                            key={cellIndex}
                        >
                            {cell}
                        </Table.Cell>)
                    }
                </Table.Row>)
            }
        </Table.Body>
    </Table>;
};

export const Cards = ({
    items
}: {
    items: Array<{
        isExternal?: boolean;
        title: string;
        href: string;
        text: string;
    }>;
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        getHref
    } = useDocsRouter();

    return <div
        style={{
            gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${shapeConfigs.contentMaxWidth / 3}px), 1fr))`,
            gap: spaces.spacingMd,
            display: "grid"
        }}
    >
        {
            items.map((item) => <Card
                hrefAttrs={item.isExternal ? {
                    rel: "noreferrer",
                    target: "_blank"
                } : undefined}
                spreadBehaviour="stretch"
                href={getHref(item.href)}
                isPressable={true}
                variant="filled"
                key={item.href}
            >
                <Card.Header>
                    <Card.Title>
                        {item.title}
                    </Card.Title>
                    <Card.Description>
                        {item.text}
                    </Card.Description>
                </Card.Header>
            </Card>)
        }
    </div>;
};

export const Stats = ({
    items
}: {
    items: Array<{
        label: string;
        value: string;
    }>;
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <div
        style={{
            gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${shapeConfigs.contentMaxWidth / 5}px), 1fr))`,
            gap: spaces.spacingMd,
            display: "grid"
        }}
    >
        {
            items.map((item) => <StatTile
                variant="filled"
                key={item.label}
            >
                <StatTile.Title>
                    {item.label}
                </StatTile.Title>
                <StatTile.Value>
                    {item.value}
                </StatTile.Value>
            </StatTile>)
        }
    </div>;
};

export const Pager = ({
    path
}: {
    path: string;
}) => {
    const {
        shapeConfigs,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const {
        getHref
    } = useDocsRouter();

    const isFullWidth = useContext(DocsWidthContext);

    const index = DOCS_PAGES.findIndex((page) => page.path === path);

    const previous = index > 0 ? DOCS_PAGES[index - 1] : undefined;

    const next = index >= 0 && index < DOCS_PAGES.length - 1 ? DOCS_PAGES[index + 1] : undefined;

    const getTitle = (item: DocsNavItem) => item.title ?? t(item.titleKey ?? "");

    const renderLink = (item: DocsNavItem, hint: string, direction: "previous" | "next") => <Card
        accessibilityLabel={`${hint}: ${getTitle(item)}`}
        spreadBehaviour="stretch"
        href={getHref(item.path)}
        isPressable={true}
        variant="filled"
    >
        <Card.Content>
            <View
                style={[
                    stylesheet.column,
                    {
                        alignItems: direction === "next" ? "flex-end" : "flex-start",
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.row,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    {
                        direction === "previous" ?
                            <Icon
                                icon={ChevronLeft}
                                color="low"
                            />
                        :
                            null
                    }
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {hint}
                    </Text>
                    {
                        direction === "next" ?
                            <Icon
                                icon={ChevronRight}
                                color="low"
                            />
                        :
                            null
                    }
                </View>
                <Text
                    variant="titleMediumSize"
                    color="high"
                >
                    {getTitle(item)}
                </Text>
            </View>
        </Card.Content>
    </Card>;

    return <nav
        style={{
            gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${shapeConfigs.contentMaxWidth / 3}px), 1fr))`,
            borderTop: `${borders.separator}px solid ${colors.content.border.subtle}`,
            maxWidth: isFullWidth ? "100%" : shapeConfigs.contentMaxWidth,
            marginTop: spaces.spacingXxl,
            paddingTop: spaces.spacingLg,
            gap: spaces.spacingMd,
            display: "grid"
        }}
        aria-label={`${t("pager.previous")} / ${t("pager.next")}`}
    >
        {
            previous ?
                renderLink(previous, t("pager.previous"), "previous")
            :
                <span/>
        }
        {next ? renderLink(next, t("pager.next"), "next") : null}
    </nav>;
};
